/* eslint-disable @typescript-eslint/member-ordering */

import {
  Component,
  computed,
  effect,
  EffectRef,
  inject,
  model,
  ModelSignal,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  viewChild,
  WritableSignal,
  ChangeDetectionStrategy
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatList } from '@angular/material/list';
import { OverlayDirective, SearchInputComponent } from '@libs/common/components';
import { CepStateService } from '@libs/shared/common';
import { CatalogSearchRepositoryService, ViewportService } from '@libs/shared/services';
import { CatalogSearchSuggestionModel } from '@libs/shop/models';
import { firstValueFrom } from 'rxjs';
import { SearchSuggestionCardComponent } from '../../catalog-search-suggestions';

@Component({
  selector: 'ui-catalog-search-input',
  templateUrl: './catalog-search-input.component.html',
  styleUrls: ['./catalog-search-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    OverlayDirective,
    SearchSuggestionCardComponent,
    SearchInputComponent,
    MatList,
  ],
})
export class CatalogSearchInputComponent {
  private readonly viewportService: ViewportService = inject(ViewportService);
  private readonly catalogSearchRepository: CatalogSearchRepositoryService = inject(CatalogSearchRepositoryService);
  private readonly cepStateService: CepStateService = inject(CepStateService);

  public readonly searchText: ModelSignal<string> = model<string>('');
  public readonly barcodeClicked: OutputEmitterRef<void> = output<void>();
  public readonly searchSubmit: OutputEmitterRef<string> = output<string>();
  public readonly searchFieldClicked: OutputEmitterRef<void> = output<void>();
  public readonly isMobile: Signal<boolean> = toSignal(this.viewportService.isMobile(), { initialValue: false });
  public readonly suggestions: WritableSignal<CatalogSearchSuggestionModel[]> = signal([]);
  public readonly overlayOpen: WritableSignal<boolean> = signal(false);

  public readonly overlayIsVisible: Signal<boolean> = computed(() => this.overlayOpen() && this.suggestions().length > 0);
  /**
   * Keeps late suggestion responses from reopening the overlay after a search was submitted.
   * It is reset by the next actual search change.
   */
  private readonly overlaySuppressed: WritableSignal<boolean> = signal(false);
  public readonly overlayDisabled: Signal<boolean> = computed(
    () => this.isMobile() || !this.suggestions().length || this.overlaySuppressed(),
  );

  private readonly overlay: Signal<OverlayDirective | undefined> = viewChild(OverlayDirective);
  private readonly searchInput: Signal<SearchInputComponent> = viewChild.required(SearchInputComponent);
  private submittedSearchText: string | undefined;

  private readonly openOverlayEffectRef: EffectRef = effect(() => {
    if (this.suggestions().length > 0 && !this.overlayDisabled()) {
      this.overlay()?.show();
    }
  });

  public onSearchFieldWrapperClicked(): void {
    if (!this.isMobile()) {
      return;
    }

    this.searchFieldClicked.emit();
  }

  public onSuggestionClicked(): void {
    this.onClose();
  }

  public onSearchFieldFocused(): void {
    if (!this.isMobile()) {
      this.overlaySuppressed.set(false);
    }

    if (this.searchText().length && !this.suggestions().length && !this.isMobile()) {
      this.loadSuggestions(this.searchText());
    }
  }

  public onSearchChanged(text: string): void {
    // SearchInputComponent can emit a debounced change that was queued before Enter.
    if (text === this.submittedSearchText) {
      this.submittedSearchText = undefined;

      return;
    }

    this.submittedSearchText = undefined;
    this.overlaySuppressed.set(false);
    this.cepStateService.updateSearchParams({
      q: text,
      category: undefined,
    });

    this.loadSuggestions(text);
  }

  public onSearchSubmit(text: string): void {
    this.submittedSearchText = text;
    this.overlaySuppressed.set(true);
    this.suggestions.set([]);
    this.searchInput().blurInput();
    this.overlay()?.close();
    this.searchSubmit.emit(text);
  }

  public onClose(): void {
    this.overlay()?.close();
  }

  public onBarcodeClicked(): void {
    this.barcodeClicked.emit();
  }

  private loadSuggestions(text: string): void {
    if (this.isMobile()) {
      return;
    }

    if (text.length >= 3) {
      void firstValueFrom(this.catalogSearchRepository.getSuggestions(text)).then((suggestionsResult) => {
        this.suggestions.set(suggestionsResult.suggestions);
      });
    } else {
      this.suggestions.set([]);
      this.onClose();
    }
  }
}
